import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";

export type ReminderStatus = "pending" | "sent" | "dismissed" | "failed";

export interface InvoiceReminder {
  id: string;
  user_id: string;
  invoice_id: string;
  reminder_type: string;
  due_date: string;
  days_until_due: number;
  status: ReminderStatus;
  created_at: string;
  sent_at: string | null;
  dismissed_at: string | null;
  recipient_email: string | null;
  email_subject: string | null;
  error_message: string | null;
  invoices: {
    invoice_number: string;
    total: number;
    clients: {
      name: string;
      email: string;
      company_name: string | null;
    } | null;
  } | null;
}

export const useInvoiceReminders = (statusFilter?: ReminderStatus) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["invoice-reminders", user?.id, statusFilter],
    queryFn: async () => {
      if (!user) throw new Error("No user");

      let query = supabase
        .from("invoice_reminders")
        .select(`
          *,
          invoices (
            invoice_number,
            total,
            clients (
              name,
              email,
              company_name
            )
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (statusFilter) {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as InvoiceReminder[];
    },
    enabled: !!user,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("invoice_reminders_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoice_reminders",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["invoice-reminders"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return query;
};

export const useSendReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reminder_id,
      custom_message,
    }: {
      reminder_id: string;
      custom_message?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "send-invoice-reminder",
        {
          body: { reminder_id, custom_message },
        }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-reminders"] });
      toast({
        title: "Reminder sent",
        description: "Payment reminder email has been sent successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send reminder",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDismissReminder = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (reminder_id: string) => {
      const { error } = await supabase
        .from("invoice_reminders")
        .update({
          status: "dismissed",
          dismissed_at: new Date().toISOString(),
        })
        .eq("id", reminder_id)
        .eq("user_id", user?.id || "");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-reminders"] });
      toast({
        title: "Reminder dismissed",
        description: "The reminder has been removed from your pending list.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to dismiss reminder",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useReminderCount = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["invoice-reminders-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;

      const { count, error } = await supabase
        .from("invoice_reminders")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("status", "pending");

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};